import React, { Component, Fragment } from "react";
import { inject, observer } from "mobx-react";
import { withRouter } from "react-router-dom";

import Grid from "@material-ui/core/Grid";
import Typography from "@material-ui/core/Typography";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import Chip from "@material-ui/core/Chip";

import currencyHelper from '../../helper/currency';
import ResponsiveTable from "../../components/table/ResponsiveTable";
import { ProfileType } from "../../types/enum";
import moment from "moment";

@inject("profileStore", "superLedgerPaymentStore")
@withRouter
@observer
class PayeePayments extends Component {
    getColumns = () => {
        const columns = [
            { key: "paymentDate", label: "Payment Date", sortable: true },
            { key: "payerName", label: "Payer", sortable: true },
            { key: "ledgerCount", label: "Items" },
            { key: "status", label: "Status" },
            { key: "source", label: "Payment Method" },
            { key: "actions", label: "Actions" }
        ];
        return columns;
    };

    getCurrencySymbol = () => {
        const { currentProfile } = this.props.profileStore;
        if (!currentProfile) return "$";
        
        let currencySymbol = "$";
        if ((currentProfile.type === ProfileType.Musician.ordinal || currentProfile.type === ProfileType.Agent.ordinal) && currentProfile.currency) {
            currencySymbol = currentProfile.currency === "GBP" ? "£" : "$";
        } else if (currentProfile.type === ProfileType.Staff.ordinal) {
            if (currentProfile.hasOrganisation) {
                currencySymbol = currentProfile.organisation?.[0]?.currency === "GBP" ? "£" : "$";
            } else {
                currencySymbol = currentProfile.venue?.currency === "GBP" ? "£" : "$";
            }
        }
        return currencySymbol;
    };

    getStatusChip = (status) => {
        const statusColors = {
            pending: { background: "#FFF3E0", color: "#E65100" },
            submitted: { background: "#E3F2FD", color: "#1565C0" },
            settled: { background: "#E8F5E9", color: "#2E7D32" },
            paid: { background: "#E8F5E9", color: "#2E7D32" },
            failed: { background: "#FFEBEE", color: "#C62828" },
            partiallypaid: { background: "#FFF8E1", color: "#F57F17" }
        };
        
        const style = statusColors[status?.toLowerCase()] || { background: "#EEEEEE", color: "#616161" };
        const s = status?.toLowerCase();
        const displayLabel = s === "partiallypaid" ? "Partially Paid" : s === "paid" ? "Paid" : (status || "Unknown");
        
        return (
            <Chip 
                label={displayLabel} 
                style={{ 
                    backgroundColor: style.background, 
                    color: style.color,
                    fontWeight: 500,
                    height: 24,
                    fontSize: 12
                }} 
            />
        );
    };

    getSourceChip = (sourceType) => {
        const sourceColors = {
            manual: { background: "#F3E5F5", color: "#7B1FA2" },
            hgSuperPay: { background: "#E8F5E9", color: "#388E3C" },
            xero: { background: "#E3F2FD", color: "#1976D2" },
            xero_payroll: { background: "#E3F2FD", color: "#1976D2" }
        };
        
        const normalizedSource = sourceType?.toLowerCase()?.replace('_', '');
        const style = sourceColors[normalizedSource] || sourceColors[sourceType?.toLowerCase()] || { background: "#EEEEEE", color: "#616161" };
        
        const sourceTypeUpper = (sourceType || "").toUpperCase();
        const displayLabel = sourceTypeUpper === "MYGIGSTERS" ? "HG Super Pay"
            : sourceTypeUpper === "XERO_PAYROLL" ? "Xero"
            : (sourceType || "Unknown");
        
        return (
            <Chip 
                label={displayLabel} 
                style={{ 
                    backgroundColor: style.background, 
                    color: style.color,
                    fontWeight: 500,
                    height: 24,
                    fontSize: 12
                }} 
            />
        );
    };

    getPayerName = (payer) => {
        if (!payer) return "-";
        
        if (Array.isArray(payer)) {
            if (payer.length === 0) return "-";
            const names = payer.map(p => {
                if (p.profileName) return p.profileName;
                const fullName = `${p.firstName || ''} ${p.lastName || ''}`.trim();
                return fullName || null;
            }).filter(Boolean);
            return names.length > 0 ? names.join(", ") : "-";
        }
        
        if (payer.profileName) return payer.profileName;
        const fullName = `${payer.firstName || ''} ${payer.lastName || ''}`.trim();
        return fullName || "-";
    };

    renderFooterRow = (payments) => {
        const currencySymbol = this.getCurrencySymbol();
        let totalAmount = 0;

        payments.forEach(payment => {
            totalAmount += parseFloat(payment.totalAmount || 0);
        });

        return {
            paymentDate: `Total (${payments.length} payments)`,
            payerName: "",
            amount: currencySymbol + currencyHelper.formatMoney(totalAmount, 2),
            ledgerCount: "",
            status: "",
            source: "",
            actions: ""
        };
    };

    render() {
        const { payeePayments, payeeHasMore, isLoading } = this.props.superLedgerPaymentStore;
        const { onLoadMore } = this.props;
        const currencySymbol = this.getCurrencySymbol();
        const columns = this.getColumns();

        return (
            <Fragment>
                <Grid container spacing={8}>
                    <Grid item xs={12} lg={12}>
                        <div>
                            <Typography variant="headline" component="h3" gutterBottom>
                                Payments Received
                            </Typography>
                            <Typography gutterBottom className="side-helper-text">
                                Payments you have received from venues, agents and other payers
                            </Typography>
                        </div>
                    </Grid>
                    <Grid item xs={12} lg={12}>
                        <Grid container spacing={16}>
                            <Grid item xs={12}>
                                <Paper>
                                    <div style={{ overflowX: "auto" }}>
                                        <ResponsiveTable
                                            columns={columns}
                                            rows={payeePayments.map((payment, index) => {
                                                if (!payment || !payment.id) return null;
                                                var row = {};
                                                row.id = payment.id;

                                                row.paymentDate = payment.paymentDate 
                                                    ? moment(payment.paymentDate).format("DD MMM YYYY")
                                                    : "-";
                                                row.payerName = this.getPayerName(payment.payer);
                                                row.amount = currencySymbol + currencyHelper.formatMoney(parseFloat(payment.totalAmount || 0), 2);
                                                row.ledgerCount = payment.ledgerCount || "-";
                                                row.status = this.getStatusChip(payment.status);
                                                row.source = this.getSourceChip(payment.sourceType);
                                                row.actions = (
                                                    <Button
                                                        variant="outlined"
                                                        color="primary"
                                                        size="small"
                                                        onClick={() => this.props.onViewPayment(payment)}
                                                    >
                                                        View
                                                    </Button>
                                                );

                                                return row;
                                            }).filter(row => row !== null)}
                                            footerRow={payeePayments.length > 0 ? this.renderFooterRow(payeePayments) : null}
                                        />
                                    </div>
                                    {payeeHasMore && (
                                        <div style={{ padding: 16, textAlign: "center" }}>
                                            <Button 
                                                variant="outlined" 
                                                color="primary" 
                                                onClick={onLoadMore}
                                                disabled={isLoading}
                                            >
                                                {isLoading ? "Loading..." : "Load More"}
                                            </Button>
                                        </div>
                                    )}
                                </Paper>
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
            </Fragment>
        );
    }
}

export default PayeePayments;
